import type { FormState, ReasoningSetting } from '../../types/editor'
import type { AgentCapabilityPreset } from '../../types/generated/AgentCapabilityPreset'

/**
 * The agent-config **v2** editing shape (see the Rust `AgentConfigV2`
 * DTO, served as JSON schema at `/api/agent-config-schema`). v2 is a
 * frontend projection: the editor works in this shape and compiles to
 * the existing create/update wire fields on save.
 */
export interface AgentConfigV2Json {
  name: string
  model?: string
  provider?: string
  system_prompt?: string
  capabilities_preset?: AgentCapabilityPreset
  capabilities?: string[]
  routing?: { hint?: string }
  stop?: {
    max_iterations?: number | 'default'
    max_run_ms?: number | null
  }
  limits?: {
    max_response_tokens?: number | 'provider_default' | null
    max_run_cost_usd?: number | null
    context_expiry_days?: number
  }
  /** In the model's own terms, e.g. `{ "effort": "xhigh" }`; `null` clears
   * it. Checked against the model when the agent is saved. */
  reasoning?: ReasoningSetting | null
}

/** FormState (+ preset) → the v2 JSON projection, omitting blank values. */
export function toV2Json(
  f: FormState,
  preset: AgentCapabilityPreset,
): AgentConfigV2Json {
  const num = (s: string): number | undefined => {
    const t = s.trim()
    if (!t) return undefined
    const n = Number(t)
    return Number.isFinite(n) ? n : undefined
  }
  const json: AgentConfigV2Json = {
    name: f.name.trim(),
    capabilities_preset: preset,
  }
  if (f.model.trim()) json.model = f.model.trim()
  if (f.provider_id.trim()) json.provider = f.provider_id.trim()
  if (f.system_prompt.trim()) json.system_prompt = f.system_prompt
  const caps = f.capabilities
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  if (caps.length > 0) json.capabilities = caps
  if (f.routing_hint.trim()) json.routing = { hint: f.routing_hint.trim() }
  const stop: AgentConfigV2Json['stop'] = {}
  const iterations = num(f.max_iterations)
  if (iterations !== undefined) stop.max_iterations = iterations
  const runMs = num(f.timeout_ms)
  if (runMs !== undefined) stop.max_run_ms = runMs
  if (Object.keys(stop).length > 0) json.stop = stop
  const limits: AgentConfigV2Json['limits'] = {}
  const tokens = num(f.max_tokens)
  if (tokens !== undefined) limits.max_response_tokens = tokens
  const cost = num(f.max_cost_usd)
  if (cost !== undefined) limits.max_run_cost_usd = cost
  const ttl = num(f.session_ttl_days)
  if (ttl !== undefined) limits.context_expiry_days = ttl
  if (Object.keys(limits).length > 0) json.limits = limits
  if (f.reasoning) json.reasoning = f.reasoning
  return json
}

/**
 * v2 JSON → FormState field patch. Fields v2 doesn't model (agent_id,
 * description, compaction, schedule) are preserved from the
 * current form. Returns the preset the JSON claims; callers flip to
 * `custom` when the capability lines diverge from it.
 */
export function applyV2Json(json: AgentConfigV2Json): {
  patch: Partial<FormState>
  preset: AgentCapabilityPreset
} {
  const patch: Partial<FormState> = {
    name: json.name ?? '',
    model: json.model ?? '',
    provider_id: json.provider ?? '',
    system_prompt: json.system_prompt ?? '',
    capabilities: (json.capabilities ?? []).join('\n'),
    routing_hint: json.routing?.hint ?? '',
    max_iterations:
      json.stop?.max_iterations != null &&
      json.stop.max_iterations !== 'default'
        ? String(json.stop.max_iterations)
        : '',
    timeout_ms:
      json.stop?.max_run_ms != null ? String(json.stop.max_run_ms) : '',
    max_tokens:
      json.limits?.max_response_tokens != null &&
      json.limits.max_response_tokens !== 'provider_default'
        ? String(json.limits.max_response_tokens)
        : '',
    max_cost_usd:
      json.limits?.max_run_cost_usd != null
        ? String(json.limits.max_run_cost_usd)
        : '',
    session_ttl_days:
      json.limits?.context_expiry_days != null
        ? String(json.limits.context_expiry_days)
        : '30',
  }
  patch.reasoning = json.reasoning ?? null
  return { patch, preset: json.capabilities_preset ?? 'custom' }
}
